import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from '@/services/logging';
import type { LogLevel, LogCategory } from '@/services/logging';

interface LoggingContextValue {
  logger: typeof logger;
  logsEnabled: boolean;
  logLevel: LogLevel;
  enabledCategories: LogCategory[];
  toggleLogging: () => void;
  setLogLevel: (level: LogLevel) => void;
  toggleCategory: (category: LogCategory) => void;
}

const LoggingContext = createContext<LoggingContextValue | undefined>(undefined);

export const LoggingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [logsEnabled, setLogsEnabled] = useState(true);
  const [logLevel, setLogLevelState] = useState<LogLevel>('debug');
  const [enabledCategories, setEnabledCategories] = useState<LogCategory[]>([
    'keyboard', 'sound', 'game', 'modal', 'navigation', 'storage', 'effects', 'shop', 'general'
  ]);

  useEffect(() => {
    // Log app initialization
    logger.info('general', 'App initialized', {
      userAgent: navigator.userAgent,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        pixelRatio: window.devicePixelRatio,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    });

    // Log performance metrics
    if ('performance' in window) {
      const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navTiming) {
        logger.info('general', 'Page load performance', {
          domContentLoaded: navTiming.domContentLoadedEventEnd - navTiming.domContentLoadedEventStart,
          loadComplete: navTiming.loadEventEnd - navTiming.loadEventStart,
          totalTime: navTiming.loadEventEnd - navTiming.fetchStart,
        });
      }
    }
  }, []);

  const toggleLogging = () => {
    const newState = !logsEnabled;
    setLogsEnabled(newState);
    logger.setEnabled(newState);
    logger.info('general', `Logging ${newState ? 'enabled' : 'disabled'}`);
  };

  const setLogLevel = (level: LogLevel) => {
    setLogLevelState(level);
    logger.setLevel(level);
    logger.info('general', `Log level changed to: ${level}`);
  };

  const toggleCategory = (category: LogCategory) => {
    if (enabledCategories.includes(category)) {
      const newCategories = enabledCategories.filter(c => c !== category);
      setEnabledCategories(newCategories);
      logger.disableCategory(category);
    } else {
      const newCategories = [...enabledCategories, category];
      setEnabledCategories(newCategories);
      logger.enableCategory(category);
    }
  };

  const value: LoggingContextValue = {
    logger,
    logsEnabled,
    logLevel,
    enabledCategories,
    toggleLogging,
    setLogLevel,
    toggleCategory,
  };

  return <LoggingContext.Provider value={value}>{children}</LoggingContext.Provider>;
};

export const useLogging = () => {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error('useLogging must be used within a LoggingProvider');
  }
  return context;
};